/**
 * Shared utility functions for social ads data processing
 *
 * This file contains shared sorting and data transformation functions
 * used across multiple hooks to ensure consistency and avoid code duplication.
 *
 * The sorting logic prioritizes ROAS (descending) first, then impressions (descending)
 * as a secondary sort criterion.
 */

import { SocialAdsAnalyticsTabular_QueryFragment } from '@frontend/gql/social/graphql';
import { TopAdData } from '../components/ads-table/ads-table-types';
import { SocialAccountData } from '../components/social-accounts-table/social-accounts-table-types';

// Type for ads data from sumInsightsBy
type AdsDataItem = NonNullable<SocialAdsAnalyticsTabular_QueryFragment['client']['sumInsightsBy']>[number];

// Type for social accounts data from sumInsightsBySocialAccounts
type SocialAccountsDataItem = NonNullable<
  SocialAdsAnalyticsTabular_QueryFragment['client']['sumInsightsBySocialAccounts']
>[number];

/**
 * Filter function to remove data where by.name or by.id is null, undefined, or empty string
 */
const filterValidAdsData = (adsData: AdsDataItem[]): AdsDataItem[] => {
  return adsData.filter((item) => {
    const by = item.by;

    // Check if by exists and has id and name properties
    if (!by || typeof by !== 'object') {
      return false;
    }

    // Check if by has id and name properties (MetaAd type)
    if ('id' in by && 'name' in by) {
      const id = by.id;
      const name = by.name;

      // Filter out if id or name is null, undefined, or empty string
      return id != null && id !== '' && name != null && name !== '';
    }

    // For other types that might not have id/name, keep them for now
    // but we could add specific filtering logic here if needed
    return true;
  });
};

/**
 * Shared function for filtering and sorting ads data
 *
 * This function performs two operations:
 * 1. Filters out invalid data where by.name or by.id is null, undefined, or empty string
 * 2. Sorts the remaining data by ROAS first (descending), then by impressions (descending)
 *
 * @param adsData - Array of ads data items to process
 * @returns Filtered and sorted array of TopAdData
 */
export function filterAndSortAdsData(adsData: AdsDataItem[]): TopAdData[] {
  // First filter out invalid data
  const validAdsData = filterValidAdsData(adsData);

  // Sort by ROAS first (descending), then by impressions (descending)
  const sortedAds = [...validAdsData].sort((a, b) => {
    const roasA = a.metrics?.roas || 0;
    const roasB = b.metrics?.roas || 0;
    const impressionsA = a.metrics?.impressions || 0;
    const impressionsB = b.metrics?.impressions || 0;

    // Primary sort: ROAS (descending)
    if (roasA !== roasB) {
      return roasB - roasA;
    }

    // Secondary sort: Impressions (descending)
    return impressionsB - impressionsA;
  });

  return sortedAds as TopAdData[];
}

/**
 * Shared sorting function for social accounts data
 * Sorts by ROAS first (descending), then by impressions (descending)
 * Note: This function does not slice the results - consumers should slice as needed
 */
export function sortSocialAccountsData(socialAccountsData: SocialAccountsDataItem[]): SocialAccountData[] {
  // Sort by ROAS first (descending), then by impressions (descending)
  const sortedSocialAccounts = [...socialAccountsData].sort((a, b) => {
    const roasA = a.metrics?.roas || 0;
    const roasB = b.metrics?.roas || 0;
    const impressionsA = a.metrics?.impressions || 0;
    const impressionsB = b.metrics?.impressions || 0;

    // Primary sort: ROAS (descending)
    if (roasA !== roasB) {
      return roasB - roasA;
    }

    // Secondary sort: Impressions (descending)
    return impressionsB - impressionsA;
  });

  return sortedSocialAccounts.map((item, index) => {
    const socialAccount = item.by;
    const metrics = item.metrics;

    // Type guard to ensure we only process MetaSocialAccount types
    if (!socialAccount || socialAccount.__typename !== 'MetaSocialAccount') {
      return {
        id: `unknown-${index}`,
        socialAccount: 'Unknown',
        memberName: 'Unknown Member',
        memberAvatar: undefined,
        memberIds: undefined,
        roas: '0.0X',
        impressions: 0,
        reach: 0,
        clicks: 0,
        ctr: undefined,
        cpm: undefined,
        cpc: undefined,
        spend: 0,
        sales: 0,
        createdAt: undefined,
        updatedAt: undefined,
      };
    }

    // Use username as the primary social account identifier
    const socialAccountName = socialAccount.username || socialAccount.fullName || 'Unknown';

    // Calculate ROAS as a string with 'X' suffix
    const roasValue = metrics.roas || 0;
    const roasString = roasValue > 0 ? `${roasValue.toFixed(1)}X` : '0.0X';

    return {
      id: socialAccount.externalId || `social-account-${index}`,
      socialAccount: socialAccountName,
      memberName: socialAccount.fullName || socialAccount.username || 'Unknown Member',
      memberAvatar: socialAccount.profilePictureUrl || undefined,
      memberIds: socialAccount.memberIds || undefined,
      roas: roasString,
      impressions: metrics.impressions || 0,
      reach: metrics.reach || 0,
      clicks: metrics.clicks || 0,
      ctr: metrics.ctr || undefined,
      cpm: metrics.cpm || undefined,
      cpc: metrics.cpc || undefined,
      spend: metrics.spend?.amount || 0,
      sales: metrics.purchase?.amount || 0,
      createdAt: undefined, // Not available in current schema
      updatedAt: undefined, // Not available in current schema
    };
  });
}

// Types for AdCampaign and AdSet
export interface AdCampaign {
  id: string;
  name: string;
  adSets?: AdSet[];
  hasPaidPartnershipAds?: boolean;
}

export interface AdSet {
  id: string;
  name: string;
}
